import {
  Alert,
  Button,
  Card,
  Flex,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { DiagnosticRule } from '~/app/services/diagnosticRules/diagnostic-rule.types';
import type { DiagnosticRulesPageModel } from './hooks/useDiagnosticRulesPage';
import { IndexingStatusTag } from './components/IndexingStatusTag/IndexingStatusTag';
import { RuleDetailDrawer } from './components/RuleDetailDrawer/RuleDetailDrawer';
import { RuleFormDrawer } from './components/RuleFormDrawer/RuleFormDrawer';
import { formatList } from './utils';

const { Title, Paragraph, Text } = Typography;

export function DiagnosticRulesPageView(page: DiagnosticRulesPageModel) {
  const { t, i18n } = useTranslation('diagnosticRules');
  const { filters, onFiltersChange } = page;

  const columns = [
    {
      title: t('columns.title'),
      dataIndex: 'title',
      render: (_: unknown, rule: DiagnosticRule) => (
        <Space direction="vertical" size={0}>
          <Button
            type="link"
            style={{ padding: 0 }}
            onClick={() => page.onOpenDetail(rule.id)}
          >
            {rule.title}
          </Button>
          {rule.alwaysApply ? (
            <Tag color="purple">{t('columns.alwaysApply')}</Tag>
          ) : null}
        </Space>
      ),
    },
    {
      title: t('columns.kind'),
      dataIndex: 'kind',
      render: (kind: string) => t(`kinds.${kind}`),
    },
    {
      title: t('columns.model'),
      key: 'models',
      render: (_: unknown, rule: DiagnosticRule) =>
        formatList(rule.assetModels.map((model) => model.name)),
    },
    {
      title: t('columns.manufacturer'),
      dataIndex: 'manufacturerName',
      render: (value: string | null) => value ?? '-',
    },
    {
      title: t('columns.standard'),
      dataIndex: 'standard',
      render: (value: string | null) => value ?? '-',
    },
    {
      title: t('columns.firmware'),
      key: 'firmware',
      render: (_: unknown, rule: DiagnosticRule) =>
        formatList(rule.firmwareVersionsRaw),
    },
    {
      title: t('columns.indexing'),
      key: 'indexing',
      render: (_: unknown, rule: DiagnosticRule) => (
        <Space>
          <IndexingStatusTag rule={rule} />
          {page.canManage && rule.indexingStatus === 'FAILED' ? (
            <Button size="small" onClick={() => page.onReindex(rule)}>
              {t('actions.retryIndexing')}
            </Button>
          ) : null}
        </Space>
      ),
    },
    { title: t('columns.version'), dataIndex: 'currentVersion' },
    {
      title: t('columns.updatedAt'),
      dataIndex: 'updatedAt',
      render: (value: string) => new Date(value).toLocaleString(i18n.language),
    },
    {
      title: t('columns.active'),
      key: 'active',
      render: (_: unknown, rule: DiagnosticRule) => (
        <Switch
          checked={rule.isActive}
          disabled={!page.canManage}
          onChange={(active) => page.onSetActive(rule, active)}
        />
      ),
    },
    ...(page.canManage
      ? [
          {
            title: '',
            key: 'actions',
            render: (_: unknown, rule: DiagnosticRule) => (
              <Button size="small" onClick={() => page.onEdit(rule)}>
                {t('actions.edit')}
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <Flex vertical gap={16} style={{ padding: 24 }}>
      <Flex justify="space-between" align="flex-start" wrap gap={12}>
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>
            {t('page.title')}
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {t('page.subtitle')}
          </Paragraph>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={page.onRefresh}>
            {t('actions.refresh')}
          </Button>
          {page.canManage ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={page.onCreate}
            >
              {t('actions.create')}
            </Button>
          ) : null}
        </Space>
      </Flex>

      <Card>
        <Flex gap={12} wrap align="center">
          {page.isSuperAdmin ? (
            <Space>
              <Text strong>{t('scope.label')}</Text>
              <Select
                style={{ minWidth: 260 }}
                showSearch
                optionFilterProp="label"
                value={page.scope}
                onChange={page.onScopeChange}
                options={[
                  { value: 'global', label: t('scope.global') },
                  ...page.organizations.map((org) => ({
                    value: org.id,
                    label: org.name,
                  })),
                ]}
              />
            </Space>
          ) : null}
          <Select
            allowClear
            placeholder={t('filters.active')}
            style={{ width: 160 }}
            value={
              filters.isActive === undefined
                ? undefined
                : String(filters.isActive)
            }
            onChange={(value) =>
              onFiltersChange({
                ...filters,
                isActive: value === undefined ? undefined : value === 'true',
              })
            }
            options={[
              { value: 'true', label: t('filters.activeOnly') },
              { value: 'false', label: t('filters.inactiveOnly') },
            ]}
          />
          <Select
            allowClear
            placeholder={t('filters.kind')}
            style={{ width: 160 }}
            value={filters.kind}
            onChange={(kind) => onFiltersChange({ ...filters, kind })}
            options={[
              { value: 'TEXT', label: t('kinds.TEXT') },
              { value: 'DOCUMENT', label: t('kinds.DOCUMENT') },
            ]}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={t('filters.model')}
            style={{ width: 200 }}
            value={filters.assetModelId}
            onChange={(assetModelId) =>
              onFiltersChange({ ...filters, assetModelId })
            }
            options={page.options.assetModels.map((model) => ({
              value: model.id,
              label: model.name,
            }))}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={t('filters.manufacturer')}
            style={{ width: 200 }}
            value={filters.manufacturerOrganizationId}
            onChange={(manufacturerOrganizationId) =>
              onFiltersChange({ ...filters, manufacturerOrganizationId })
            }
            options={page.options.manufacturers.map((maker) => ({
              value: maker.id,
              label: maker.name,
            }))}
          />
          <Select
            allowClear
            placeholder={t('filters.standard')}
            style={{ width: 140 }}
            value={filters.standard}
            onChange={(standard) => onFiltersChange({ ...filters, standard })}
            options={[
              { value: 'AC', label: 'AC' },
              { value: 'DC', label: 'DC' },
            ]}
          />
        </Flex>
      </Card>

      {page.error ? (
        <Alert type="error" showIcon message={page.error.message} />
      ) : null}

      <Table<DiagnosticRule>
        rowKey="id"
        loading={page.isLoading}
        columns={columns}
        dataSource={page.rules}
        locale={{ emptyText: t('page.empty') }}
        scroll={{ x: true }}
      />

      <RuleFormDrawer
        open={page.drawer.mode !== 'closed'}
        rule={page.drawer.mode === 'edit' ? page.drawer.rule : null}
        options={page.options}
        isSaving={page.isSaving}
        onClose={page.onCloseDrawer}
        onSave={page.onSave}
      />
      <RuleDetailDrawer
        open={page.isDetailOpen}
        detail={page.detail}
        isLoading={page.isDetailLoading}
        onClose={page.onCloseDetail}
      />
    </Flex>
  );
}
